import { Popover } from '@/components/ats';
import { HeaderCell } from '@/components/ats/Table/tableCells';
import { useShowToast } from '@/hooks';
import {
  useDeleteScheduleReportMutation,
  useUpdateScheduleReportMutation,
} from '@/store/query/ats-reports/scheduleReportsEmail';
import { selectUsers } from '@/store/users/selectors';
import { KText } from '@/ui-library/components';
import { TruncatedTextWithTooltip } from '@/ui/TooltipText';
import {
  Avatar,
  AvatarGroup,
  Box,
  Divider,
  Flex,
  Text,
  Tooltip,
} from '@chakra-ui/react';
import React from 'react';
import { useSelector } from 'react-redux';

export const useScheduleTableConfig = ({
  setCurrentView,
  setSelectedReport,
}) => {
  interface CellProps {
    row?: {
      original?: {
        id?: string | number;
        email?: string;
        first_name?: string;
        last_name?: string;
        avatar_thumb_url?: string;
        intelligence_status?: string;
        start_time?: string;
        end_time?: string;
        recipients?: any[];
        external_recipient_emails?: any[];
      };
    };
    value?: any;
  }

  const defaultCellValue: CellProps = {
    row: {
      original: {},
    },
    value: undefined,
  };

  const [deleteReport, { isLoading: isDeleting }] =
    useDeleteScheduleReportMutation();
  const [updateReport] = useUpdateScheduleReportMutation();

  const toast = useShowToast();
  const users = useSelector(selectUsers);

  const rowConfig = [
    {
      id: 'edit',
      label: 'Edit',
      enabled: true,
      onClick: (onClose, currentClickedRow) => {
        setSelectedReport(currentClickedRow);
        setCurrentView('create/edit');
        onClose();
      },
    },
    {
      id: 'pause/active',
      setLabel: (currentRow) => {
        return currentRow?.status === 'active' ? 'Pause' : 'Activate';
      },
      enabled: true,
      onClick: (onClose, currentRow) => {
        try {
          updateReport({
            id: currentRow.id,
            payload: {
              ...currentRow,
              status: currentRow?.status === 'active' ? 'paused' : 'active',
            },
          }).then((response) => {
            //@ts-ignore
            const { error } = response || {};
            if (error?.length > 0 && error?.[0]) {
              toast({
                title:
                  currentRow?.status === 'active'
                    ? 'Unable to pause the email schedule'
                    : 'Unable to activate the email schedule',
                description: error?.[0],
                status: 'error',
              });
            } else {
              toast({
                title:
                  currentRow?.status === 'active'
                    ? 'Email schedule paused successfully'
                    : 'Email schedule activated successfully',
              });
              onClose();
            }
          });
        } catch (err) {
          toast({
            title: 'Unexpected error',
            description: err.message,
            status: 'error',
          });
        } finally {
        }
      },
    },
    {
      id: 'delete',
      label: 'Delete',
      enabled: true,
      modalConfig: {
        title: 'Are you sure?',
        description: `Deleting an email schedule is permanent and cannot be undone`,
        successButtonText: 'Yes, delete',
        cancelButtonText: 'Cancel',
        isActionLoading: false,
        handleOnClose: (onClose) => {
          onClose();
        },
        isModalActionLoading: isDeleting,
        isButtonDisabled: false,
        onModalSubmit: async (selected, onClose, loadingAction) => {
          loadingAction(true);
          try {
            deleteReport({ id: selected?.id }).then((response) => {
              //@ts-ignore
              const { error } = response || {};
              if (error?.length > 0 && error?.[0]) {
                toast({
                  title: 'Unable to delete the email schedule',
                  description: error?.[0],
                  status: 'error',
                });
                loadingAction(false);
              } else {
                toast({ title: 'Email schedule deleted successfully' });
                onClose();
              }
            });
          } catch {
          } finally {
            onClose();
            loadingAction(false);
          }
        },
      },
    },
  ];

  const columnConfig = [
    {
      Header: () => <HeaderCell title="List of schedules" />,
      width: 280,
      accessor: 'name',
      sortable: true,
      filterable: true,
      title: 'List of schedules',
      columnConfig: false,
      Cell: (cell: CellProps = defaultCellValue) => {
        const {
          value,
          row: { original },
        } = cell;

        return (
          <TruncatedTextWithTooltip
            maxW={'260px'}
            size="sm-medium"
            toolTipVariant="default"
            textTransform="capitalize"
            text={value}
            cursor="pointer"
            _hover={{
              color: 'ats-primary',
            }}
            onClick={() => {
              setCurrentView('create/edit');
              setSelectedReport(original);
            }}
          />
        );
      },
    },
    {
      Header: () => <HeaderCell title="Frequency" />,
      width: 120,
      accessor: 'frequency',
      sortable: true,
      filterable: true,
      title: 'Frequency',
      columnConfig: false,
      Cell: (cell: CellProps = defaultCellValue) => {
        const { value } = cell;

        return (
          <TruncatedTextWithTooltip
            maxW={'110px'}
            size="sm-medium"
            toolTipVariant="default"
            textTransform="capitalize"
            text={value}
            color="ats-text-secondary"
            p="0.125em 0.5em"
            bgColor="ats-tertiary"
            borderRadius="4px"
            border="1px solid"
            borderColor="transparent"
            _groupHover={{ bg: '#fff', borderColor: 'border-color' }}
          />
        );
      },
    },
    {
      Header: () => <HeaderCell title="Filters" />,
      width: 150,
      accessor: 'metadata',
      sortable: true,
      filterable: true,
      title: 'Filters',
      columnConfig: false,
      Cell: (cell: CellProps = defaultCellValue) => {
        const { filtersToRender: filters = [] } = cell?.value || [];

        if (Array?.isArray(filters) && filters?.length === 0) {
          return (
            <TruncatedTextWithTooltip
              maxW={'110px'}
              size="sm-medium"
              toolTipVariant="default"
              text={'No filters'}
              color="ats-text-secondary"
              p="0.125em 0.5em"
              bgColor="ats-tertiary"
              borderRadius="4px"
              border="1px solid"
              borderColor="transparent"
              _groupHover={{ bg: '#fff', borderColor: 'border-color' }}
            />
          );
        }

        return (
          <>
            <Popover
              triggerElement={
                <Text
                  size="sm-medium"
                  color="ats-text-secondary"
                  p="0.125em 0.5em"
                  bgColor="ats-tertiary"
                  borderRadius="4px"
                  border="1px solid"
                  borderColor="transparent"
                  cursor="pointer"
                  _groupHover={{
                    bg: '#fff',
                    borderColor: 'border-color',
                  }}
                >
                  {`${filters?.length} filters`}
                </Text>
              }
              popoverProps={{
                trigger: 'hover',
              }}
              showCloseButton={false}
            >
              <Flex p={3} flexDir="column">
                {Array?.isArray(filters) &&
                  filters.map((filter, index) => (
                    <Box key={index}>
                      <Text fontSize="sm">
                        <Text as="span" size="sm-semi-bold">
                          {filter.filter_by.label}{' '}
                          {filter.filter_condition.label}:{' '}
                        </Text>
                        {filter.filter_by_values
                          .map(
                            (value) => value.title || value.name || value.label
                          )
                          .join(', ')}
                      </Text>
                      {index < filters.length - 1 && <Divider my={2} />}
                    </Box>
                  ))}
              </Flex>
            </Popover>
          </>
        );
      },
    },
    {
      Header: () => <HeaderCell title="Recipients" />,
      width: 150,
      accessor: 'recipients',
      sortable: true,
      filterable: true,
      title: 'Recipients',
      columnConfig: false,
      Cell: (cell: CellProps = defaultCellValue) => {
        const ids = cell?.value?.map((i) => i?.id);
        const { row } = cell;
        const { original } = row || {};
        const { external_recipient_emails } = original || {};
        const usersArray = users.filter((obj) => ids.includes(obj.id));

        // Combine internal users and external emails with proper formatting
        const allRecipients = [
          ...usersArray.map((user) => ({ type: 'user', data: user })),
          ...(external_recipient_emails || []).map((email) => ({
            type: 'email',
            data: email,
          })),
        ];

        const numberOfRecipientsToShow = 3;
        const displayedRecipients = allRecipients.slice(
          0,
          numberOfRecipientsToShow
        );
        const remainingRecipients =
          allRecipients.length - numberOfRecipientsToShow;

        return (
          <Flex gap="5px" alignItems="center">
            <AvatarGroup size="xs">
              {displayedRecipients?.map((recipient, index) => (
                <Tooltip
                  key={`${recipient.type}-${recipient.data}-${index}`}
                  label={
                    recipient.type === 'user'
                      ? recipient.data?.name
                      : `${recipient.data} (External)`
                  }
                  variant="ats-primary"
                  hasArrow
                >
                  <Box>
                    {recipient.type === 'user' ? (
                      <Avatar
                        name={recipient.data?.name}
                        src={recipient.data?.avatar}
                        borderRadius="8px"
                        size="xs"
                        ml={index > 0 ? '-5px' : 0}
                        borderRight="2px solid white"
                      />
                    ) : (
                      <Avatar
                        name={recipient.data}
                        size="xs"
                        borderRadius="8px"
                        ml={index > 0 ? '-5px' : 0}
                        borderRight="2px solid white"
                      />
                    )}
                  </Box>
                </Tooltip>
              ))}
            </AvatarGroup>
            {remainingRecipients > 0 && (
              <Popover
                triggerElement={
                  <Text
                    cursor="pointer"
                    fontSize="sm"
                    bg="secondary-50"
                    p="0.125em 0.5em"
                    borderRadius="4px"
                    border="1px solid"
                    borderColor="transparent"
                    _groupHover={{ bg: '#fff', borderColor: 'border-color' }}
                  >
                    +{remainingRecipients}
                  </Text>
                }
                popoverProps={{
                  trigger: 'hover',
                }}
                showCloseButton={false}
              >
                <Box>
                  {allRecipients
                    .slice(numberOfRecipientsToShow)
                    .map((recipient, index) => (
                      <Flex key={index} align="center" mb="2">
                        {recipient.type === 'user' ? (
                          <>
                            <Avatar
                              name={recipient.data?.name}
                              src={recipient.data?.avatar}
                              size="xs"
                              borderRadius="8px"
                              mr="2"
                            />
                            <Text fontSize="sm">{recipient.data?.name}</Text>
                          </>
                        ) : (
                          <>
                            <Avatar
                              name={recipient.data}
                              size="xs"
                              borderRadius="8px"
                              mr="2"
                              bg="secondary-100"
                              color="secondary-700"
                            />
                            <KText>
                              {recipient.data}{' '}
                              <KText color="secondary-500" display="inline">
                                (External)
                              </KText>
                            </KText>
                          </>
                        )}
                      </Flex>
                    ))}
                </Box>
              </Popover>
            )}
          </Flex>
        );
      },
    },
    {
      Header: () => <HeaderCell title="Status" />,
      width: 120,
      accessor: 'status',
      sortable: true,
      filterable: true,
      title: 'Status',
      columnConfig: false,
      Cell: (cell) => {
        const statusMap = {
          active: {
            color: '#73D017',
            label: 'Active',
          },
          paused: { color: '#F8B147', label: 'Paused' },
        };

        const { color, label } = statusMap[cell?.value] || {};

        return (
          <Flex
            gap="0.5em"
            alignItems="center"
            border={'1px solid'}
            borderRadius="6px"
            p="0.125em 0.5em"
            borderColor="border-color"
            _groupHover={{ bg: '#fff' }}
          >
            <Box w="8px" h="8px" bgColor={color} borderRadius="10px"></Box>
            <Text color="ats-text-primary" size="sm-medium">
              {label}
            </Text>
          </Flex>
        );
      },
    },
  ];

  return {
    columnConfig,
    rowConfig,
  };
};
